import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface ChatRoomMemberInfo {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  last_read_at: string | null;
}

export interface ChatRoom {
  id: string;
  type: "direct" | "group" | "house";
  name: string | null;
  project_id: string | null;
  created_at: string;
  last_message_at: string | null;
  members: ChatRoomMemberInfo[];
  unreadCount: number;
  /** VIB-3025: en ulest melding nevner brukeren (@-merke i Chat-rommet). */
  hasUnreadMention: boolean;
  /** For direct-rooms: the "other" user for display. Null for group. */
  otherUser: ChatRoomMemberInfo | null;
  lastMessagePreview: string | null;
}

interface RawRoomRow {
  id: string;
  type: "direct" | "group" | "house";
  name: string | null;
  project_id: string | null;
  created_at: string;
  last_message_at: string | null;
  chat_members: Array<{
    user_id: string;
    last_read_at: string | null;
    users: { full_name: string; avatar_url: string | null } | null;
  }>;
}

async function fetchRoomsForUser(userId: string): Promise<ChatRoom[]> {
  // 1) Rom-IDer der brukeren er medlem
  const { data: memberRows, error: memberErr } = await supabase
    .from("chat_members")
    .select("room_id, last_read_at")
    .eq("user_id", userId);
  if (memberErr) throw memberErr;

  const roomIds = (memberRows ?? []).map((m) => m.room_id);
  if (roomIds.length === 0) return [];

  // 2) Alle rom med medlemmer
  const { data: rooms, error: roomsErr } = await supabase
    .from("chat_rooms")
    .select(`
      id, type, name, project_id, created_at, last_message_at,
      chat_members (
        user_id,
        last_read_at,
        users ( full_name, avatar_url )
      )
    `)
    .in("id", roomIds)
    .order("last_message_at", { ascending: false, nullsFirst: false });
  if (roomsErr) throw roomsErr;

  // 3) Ulest-telling + siste preview
  const userLastRead = new Map<string, string | null>();
  for (const m of memberRows ?? []) userLastRead.set(m.room_id, m.last_read_at);

  const unreadByRoom = new Map<string, number>();
  const previewByRoom = new Map<string, string>();
  const mentionedRooms = new Set<string>();

  const { data: recentMessages, error: msgErr } = await supabase
    .from("chat_messages")
    // `*` i stedet for navngitte kolonner: mentioned_user_ids (VIB-3025) kan
    // mangle et øyeblikk hvis frontenden deployes før migrasjonen.
    .select("*")
    .in("room_id", roomIds)
    .is("deleted_at", null)
    .order("created_at", { ascending: false })
    .limit(500);
  if (msgErr) throw msgErr;

  for (const m of recentMessages ?? []) {
    if (!previewByRoom.has(m.room_id)) {
      previewByRoom.set(m.room_id, m.body ?? (m.attachment_name ? `📎 ${m.attachment_name}` : ""));
    }
    const lastRead = userLastRead.get(m.room_id);
    if (m.sender_id !== userId && (!lastRead || m.created_at > lastRead)) {
      unreadByRoom.set(m.room_id, (unreadByRoom.get(m.room_id) ?? 0) + 1);
      if ((m.mentioned_user_ids ?? []).includes(userId)) mentionedRooms.add(m.room_id);
    }
  }

  return (rooms as unknown as RawRoomRow[]).map((r) => {
    const members: ChatRoomMemberInfo[] = (r.chat_members ?? []).map((cm) => ({
      user_id: cm.user_id,
      full_name: cm.users?.full_name ?? null,
      avatar_url: cm.users?.avatar_url ?? null,
      last_read_at: cm.last_read_at,
    }));
    const otherUser = r.type === "direct" ? members.find((m) => m.user_id !== userId) ?? null : null;
    return {
      id: r.id,
      type: r.type,
      name: r.name,
      project_id: r.project_id,
      created_at: r.created_at,
      last_message_at: r.last_message_at,
      members,
      unreadCount: unreadByRoom.get(r.id) ?? 0,
      hasUnreadMention: mentionedRooms.has(r.id),
      otherUser,
      lastMessagePreview: previewByRoom.get(r.id) ?? null,
    };
  });
}

export function useChatRooms(userId: string | null | undefined) {
  const query = useQuery({
    queryKey: ["chat-rooms", userId],
    enabled: !!userId,
    queryFn: () => fetchRoomsForUser(userId!),
  });

  // Realtime: sentralisert via useRealtimeListeners (REALTIME_TABLES).
  // chat_messages, chat_rooms og chat_members invaliderer alle ["chat-rooms", userId]
  // via CACHE_REGISTRY-fan-out.

  return query;
}

/** Total ulest-badge-tall på tvers av alle rom */
export function useChatUnreadTotal(userId: string | null | undefined): number {
  const { data } = useChatRooms(userId);
  return (data ?? []).reduce((sum, r) => sum + r.unreadCount, 0);
}
